import moment from "moment";
import "moment-timezone";
import store from "../store"; // Access redux store data
import { getLocationTimezone, getLocationToday } from "./functions";
/******************* 
@purpose : Check date is current day date
@Parameter : {date}
@Author : INIC
******************/
export const isTodayDate = (date) => {
  const today = new Date();
  if (date) {
    return (
      date.getDate() == today.getDate() &&
      date.getMonth() == today.getMonth() &&
      date.getFullYear() == today.getFullYear()
    );
  } else {
    return false;
  }
};

/******************* 
@purpose : Check date in weekend
@Parameter : {date}
@Author : INIC
******************/
export const isWeekendDate = (date) => {
  if (date) {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 6 || dayOfWeek === 0;
  } else {
    return false;
  }
};

/******************* 
@purpose : Time options
@Author : INIC
******************/
export const getTimeOptions = (flag) => {
  const vehicleAvailableForDriveNow =
    store?.getState()?.vehicle?.vehicleAvailableForDriveNow;
  let startIndex = 0;
  let loopLen = 13;
  const idArr = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
  const labelArr = [
    "8:00 AM",
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
    "6:00 PM",
    "7:00 PM",
    "8:00 PM",
  ];
  const options = [];

  if (flag == "today" || flag == "todayweekend") {
    const currentHour = new Date().getHours();
    const currentMinute = new Date().getMinutes();
    let checkEndTime = 20;
    if (flag == "todayweekend") {
      checkEndTime = 18;
    }
    if (currentHour + 2 > checkEndTime) {
      loopLen = 13;
      // no time selection
      startIndex = 13;
    } else {
      if (currentHour < 7) {
        startIndex = 0;
      } else if (currentHour == 7) {
        startIndex = 1;
      } else {
        if (vehicleAvailableForDriveNow) {
          if (currentMinute < 31) startIndex = currentHour - 8 + 1;
          else startIndex = currentHour - 8 + 2;
        } else {
          if (currentMinute < 1) startIndex = currentHour - 8 + 2;
          else startIndex = currentHour - 8 + 3;
        }

        /*
          if (currentMinute < 31) {
            if (vehicleAvailableForDriveNow) {
              startIndex = currentHour - 8 + 1;
            } else {
              startIndex = currentHour - 8 + 2;
            }
          } else {
            if (vehicleAvailableForDriveNow) {
              startIndex = currentHour - 8 + 2;
            } else {
              startIndex = currentHour - 8 + 3;
            }
          }*/
      }
    }
    if (flag != "today") {
      loopLen = 11;
    }
  } else if (flag == "weekend") {
    loopLen = 11;
  } else {
    loopLen = 13;
  }
  for (let i = startIndex; i < loopLen; i++) {
    const obj = {
      id: idArr[i],
      label: labelArr[i],
    };
    options.push(obj);
  }
  return options;
};

/*******************
@purpose : Minimum lead time (hours) between "now" and the earliest bookable
           pickup slot. Kept in sync with the vehicle TimePicker.
@Author : INIC
******************/
export const MIN_BOOKING_LEAD_HOURS = 2;

/*******************
@purpose : Hour (exclusive) that a slot on the given date must be after to
           still be bookable, evaluated in the location's timezone.
           Past dates -> Infinity (nothing bookable), future dates ->
           -Infinity (everything bookable), today -> now + lead time.
           With the vehicle's `earliest_start` (Drive Now), that time
           replaces the lead time: days before it close, its day opens at it.
@Parameter : {date} "MMM d, yyyy" or "YYYY-MM-DD", {earliestStart} "YYYY-MM-DD HH:mm:ss"
@Author : INIC
******************/
export const getMinBookableHour = (date, earliestStart = null) => {
  if (!date) return -Infinity;
  const selected = moment(date, ["MMM D, YYYY", "YYYY-MM-DD"]);
  if (!selected.isValid()) return -Infinity;

  const nowInTz = moment().tz(getLocationTimezone());
  const selectedDay = selected.format("YYYY-MM-DD");
  const today = nowInTz.format("YYYY-MM-DD");

  if (selectedDay < today) return Infinity;

  const earliest = parseEarliestStart(earliestStart);
  if (earliest) {
    const earliestDay = earliest.format("YYYY-MM-DD");
    if (selectedDay < earliestDay) return Infinity;
    if (selectedDay > earliestDay) return -Infinity;
    // Slots are on the hour, so a start at 11:30 opens from 12:00.
    return earliest.hours() - (earliest.minutes() > 0 ? 0 : 1);
  }

  if (selectedDay > today) return -Infinity;
  return nowInTz.hours() + MIN_BOOKING_LEAD_HOURS;
};

/*******************
@purpose : the API's `earliest_start` (location wall time) as a moment, or null
@Parameter : {earliestStart} "YYYY-MM-DD HH:mm:ss"
@Author : INIC
******************/
export const parseEarliestStart = (earliestStart) => {
  if (!earliestStart) return null;
  const earliest = moment(earliestStart, "YYYY-MM-DD HH:mm:ss", true);
  return earliest.isValid() ? earliest : null;
};

/*******************
@purpose : "11:00 AM today" / "9:00 AM tomorrow" / "9:00 AM Tue, Mar 16" for a
           location wall time, judged against today in the location's timezone
@Parameter : {dateTime} "YYYY-MM-DD HH:mm:ss"
@Author : INIC
******************/
export const formatLocationDayTime = (dateTime) => {
  const value = parseEarliestStart(dateTime);
  if (!value) return "";

  const today = moment.tz(getLocationTimezone()).format("YYYY-MM-DD");
  const tomorrow = moment.tz(getLocationTimezone()).add(1, "day").format("YYYY-MM-DD");
  const day = value.format("YYYY-MM-DD");
  const time = value.format("h:mm A");

  if (day === today) return `${time} today`;
  if (day === tomorrow) return `${time} tomorrow`;
  return `${time} ${value.format("ddd, MMM D")}`;
};

/*******************
@purpose : Whether a "H:00" time on the given date is still bookable
@Parameter : {date, time, earliestStart}
@Author : INIC
******************/
export const isTimeBookable = (date, time, earliestStart = null) => {
  const hour = parseInt(String(time || "").split(":")[0], 10);
  if (isNaN(hour)) return false;
  return hour > getMinBookableHour(date, earliestStart);
};

/*******************
@purpose : Search-bar time options for a date. All slots are returned, but
           slots in the past (or inside the lead time) and, optionally, not
           after a given hour (used for a same-day end time) are flagged
           `disabled` so the dropdown shows them greyed out.
@Parameter : {date, afterHour}
@Author : INIC
******************/
export const getBookableTimeOptions = (date, afterHour = null) => {
  const minHour = getMinBookableHour(date);
  return getTimeOptions("other").map((option) => ({
    ...option,
    disabled: !(
      option.id > minHour && (afterHour === null || option.id > afterHour)
    ),
  }));
};

/*******************
@purpose : Keep the current "H:00" value if it is still a selectable option,
           otherwise fall back to 10:00 / 9:00 / 8:00 or the first
           selectable option
@Parameter : {options, current}
@Author : INIC
******************/
export const pickBookableTime = (options, current) => {
  const values = options
    .filter((option) => !option.disabled)
    .map((option) => `${option.id}:00`);
  if (current && values.includes(current)) return current;
  return ["10:00", "9:00", "8:00"].find((t) => values.includes(t)) || values[0] || "";
};

/******************* 
@purpose : break down logic
@Author : INIC
******************/
export const breakDown = (
  vehicalDetailData,
  dateObj,
  startDateDefaultValue,
  endDateDefaultValue,
  dateOfBirth,
  globalSetting = "",
  ageParameter = 0,
  deliveryFee,
  returnFee
) => {
  let startTime = typeof startDateDefaultValue === "string" ? startDateDefaultValue.split(":") : [];
  let endTime = typeof endDateDefaultValue === "string" ? endDateDefaultValue.split(":") : [];
  let perDayCharge = vehicalDetailData?.day;
  let perHourCharge = Math.round(Math.abs(perDayCharge / 5));
  let days = 0;
  let hours = 0;
  let tempHours = (parseInt(endTime[0], 10) || 0) - (parseInt(startTime[0], 10) || 0);
  let rentalCharge = 0;
  let totalHourCharge = 0;
  let monthlyDiscountPercent = 30;
  let monthlyDiscount = 0;
  let weeklyDiscountPercent = 15;
  let weeklyDiscount = 0;
  let underageFeePercent = 0;
  let underageFee = 0;
  let subTotal = 0;
  let taxesFeesPercent = 9; // taxes fee
  let taxesFees = 0;
  let totalCharge = 0;
  let age = 0;
  let securityDepositAdmin = 0;
  let vehicleProtectionValue = 0;
  let deductible = 0;

  if (ageParameter) {
    age = parseInt(ageParameter);
  }

  // days count
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  days = Math.round(
    Math.abs((dateObj[0].startDate - dateObj[0].endDate) / oneDay)
  );
  if (days == 0) {
    days = 1;
  }

  if (
    dateObj[0].startDate &&
    dateObj[0].startDate.getDate() == dateObj[0].endDate.getDate()
  ) {
    hours = 0;
  } else {
    if (tempHours > 4) {
      days = days + 1;
    } else if (tempHours > 0 && tempHours < 5) {
      hours = tempHours;
    } else {
      hours = 0;
    }
  }

  rentalCharge = Math.round(perDayCharge * days);
  totalHourCharge = Math.round(perHourCharge * hours);
  subTotal = rentalCharge + totalHourCharge;
  if (days > 27) {
    monthlyDiscount = Math.round((subTotal * monthlyDiscountPercent) / 100);
    subTotal = subTotal - monthlyDiscount;
  } else if (days > 6) {
    weeklyDiscount = Math.round((subTotal * weeklyDiscountPercent) / 100);
    subTotal = subTotal - weeklyDiscount;
  }

  if (dateOfBirth) {
    age = calculateAge(dateOfBirth);
  }

  if (globalSetting) {
    if (age > 24) {
      underageFeePercent = globalSetting?.under_age_25;
    } else if (age > 20) {
      underageFeePercent = globalSetting?.under_age_21_24;
    } else if (age > 7) {
      underageFeePercent = globalSetting?.under_age_18_20;
    } else {
      underageFeePercent = 0;
    }
  } else {
    if (age > 24) {
      underageFeePercent = 0;
    } else if (age > 20) {
      underageFeePercent = 10;
    } else if (age > 7) {
      underageFeePercent = 20;
    } else {
      underageFeePercent = 0;
    }
  }

  if (underageFeePercent > 0) {
    underageFee = Math.round((subTotal * underageFeePercent) / 100);
    subTotal = subTotal + underageFee;
  }

  taxesFees = Math.round((subTotal * taxesFeesPercent) / 100);
  totalCharge = Math.round(
    subTotal + taxesFees + (returnFee || 0) + (deliveryFee || 0)
  );
  let returnObj = {
    days,
    hours,
    perDayCharge,
    rentalCharge,
    perHourCharge,
    totalHourCharge,
    weeklyDiscount,
    weeklyDiscountPercent,
    monthlyDiscount,
    monthlyDiscountPercent,
    age,
    underageFeePercent,
    underageFee,
    taxesFeesPercent,
    taxesFees,
    subTotal,
    totalCharge,
    deliveryFee,
    securityDepositAdmin,
    vehicleProtectionValue,
    deductible,
    returnFee,
  };
  return returnObj;
};

/******************* 
@purpose : calculate age
@Parameter : {date}
@Author : INIC
******************/
export const calculateAge = (date) => {
  let dob = new Date(date);
  //calculate month difference from current date in time
  let monthDiff = Date.now() - dob.getTime();
  //convert the calculated difference in date format
  let ageDt = new Date(monthDiff);
  //extract year from date
  let year = ageDt.getUTCFullYear();
  //now calculate the age of the user
  return Math.abs(year - 1970);
};

/******************* 
@purpose : set index to am or pm
@Parameter : {time}
@Author : INIC
******************/
export const setTimeAMPM = (time) => {
  if (time < 12) {
    return time + ":00 AM";
  } else {
    if (time == 12) {
      return time + ":00 PM";
    } else {
      let timeCount = time - 12;
      return timeCount + ":00 PM";
    }
  }
};

/******************* 
@purpose : set index to am or pm
@Parameter : {time}
@Author : INIC
******************/
export const setTimeAMPMWithoutZero = (time) => {
  if (time < 12) {
    return time + ":00 AM";
  } else {
    if (time == 12) {
      return time + ":00 PM";
    } else {
      let timeCount = time - 12;
      return timeCount + ":00 PM";
    }
  }
};

/******************* 
@purpose : Check date is less than today
@Parameter : {date}
@Author : INIC
******************/
export const isDateLessThanToday = (date) => {
  // "Today" is the rental location's day, not the browser's
  if (!date) return false;
  const selected = date.includes("-")
    ? moment(date, "YYYY-MM-DD")
    : moment(new Date(date));
  if (!selected.isValid()) return false;
  return selected.format("YYYY-MM-DD") < getLocationToday();
};

/******************* 
@purpose : State Name change california to  CA, florida to FL(In furure)
@Parameter : {str}
@Author : INIC
******************/
export const changeStateNameToSmall = (str, stateName) => {
  if (str) {
    if (str.toLowerCase().includes("california")) {
      return str.replace(/california/gi, "CA");
    } else {
      return str.replace(/florida/gi, "FL");
    }
  } else {
    if (stateName) {
      if (stateName?.toLowerCase()?.includes("florida")) {
        return "FL";
      } else {
        return "CA";
      }
    } else {
      return "CA";
    }
  }
};
